// Third party
import React from 'react';

// Utils
import { canAccessUploadCSV } from '@/utils/feature-access';
import { formatMessageWithLocale, FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';

// Components
import LinkLogo from '@/views/components/link/LinkLogo';
import LinkHeader from '@/views/components/link/LinkHeader';
import User from '@/types/user';
import EmptyComparisonButton from '../misc/EmptyComparisonButton';

// Assets
import CreatePortfolioIcon from '@/images/icons/create-portfolio.svg';
import UploadPortfolioIcon from '@/images/icons/upload-portfolio.svg';

// Legacy support
import LegacyComparisonQuickSearch from '@/views/containers/comparison/misc/LegacyComparisonQuickSearch';

type Props = {
  user: User;
  showEditPortfolioModal: () => void;
  showPortfolioUploadModal: () => void;
};

const LinkNewComparison = (props: Props) => {
  const options = [
    {
      onClick: () => props.showEditPortfolioModal(),
      icon: CreatePortfolioIcon,
      title: formatMessageWithLocale('EmptyComparison.CreateNewPortfolio.Title'),
      subtitle: formatMessageWithLocale('EmptyComparison.CreateNewPortfolio.Subtitle'),
      cy: 'emptyComparison-linkComparisonButton-createClientPortfolio',
      hidden: !userHasPermissionEnabled(props.user, PERMISSIONS.PORTFOLIOS_CREATE),
    },
    {
      onClick: props.showPortfolioUploadModal,
      title: formatMessageWithLocale('EmptyComparison.UploadPortfolio.Title'),
      subtitle: formatMessageWithLocale('EmptyComparison.UploadPortfolio.Subtitle'),
      icon: UploadPortfolioIcon,
      cy: 'emptyComparison-linkComparisonButton-uploadPortfolio',
      hidden: !canAccessUploadCSV(props.user),
    },
  ].filter(option => !option.hidden);

  const filteredOptions = options.filter(i => !i.hidden);

  return (
    <div className="link-comparison">
      <LinkHeader user={props.user} middleItems={<LinkLogo user={props.user} />} />

      <div className="link-comparison-container">
        <div className="link-comparison-content">
          <header>
            <h1>
              <FormattedMessage id="LinkEmptyComparison.Title" />
            </h1>
            <p className="subtitle">
              <FormattedMessage id="EmptyComparison.Subtitle" />
            </p>
          </header>

          {/* TODO: The v2 component 'ComparisonQuickSearch' has been created and requires implementation and testing. */}
          <LegacyComparisonQuickSearch user={props.user} />

          <div className="divider-with-label">
            <div className="line-container" aria-hidden>
              <div className="line" />
            </div>
            <div className="label-container">
              <span className="label">{formatMessageWithLocale('LinkEmptyComparison.OrStartWith')}</span>
            </div>
          </div>

          <div className="empty-options" style={{ '--option-count': filteredOptions.length > 3 ? 3 : filteredOptions.length } as React.CSSProperties}>
            {filteredOptions.map((option, index) => (
              <EmptyComparisonButton key={index} onClick={option.onClick} title={option.title} subtitle={option.subtitle} icon={option.icon} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkNewComparison;
